/**
 * MyPage — a personal landing page with a single "My Applications" tab.
 *
 * The tab shows two sections:
 *   1. My Favorites            — apps the user has favorited in the marketplace.
 *   2. My Published Applications — apps where the user's alias, or any of their
 *      direct/indirect reports, is an App Builder, GRC Control Owner, or
 *      Process Owner. Ownership resolution lives in MyApplicationsUtils.js
 *      (option B: live, cached supervisor-chain lookups — no recursion).
 *
 * Card rendering, favorites, and ratings reuse the same helpers as the
 * Application Repository (Apps.jsx) so cards look and behave identically.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import {
  AppLayout,
  Box,
  Cards,
  Container,
  Header,
  Pagination,
  ProgressBar,
  SpaceBetween,
  Tabs,
  TextContent,
} from "@amzn/awsui-components-react";

import "./homepage.scss";

// Cards are laid out 3 per row (see cardsPerRow below). Each section is paged
// 2 rows at a time; the pager only shows when there is more than one page
// (i.e. more than 2 rows of cards). Every container paginates independently.
const CARDS_PER_ROW = 3;
const ROWS_PER_PAGE = 2;
const PAGE_SIZE = CARDS_PER_ROW * ROWS_PER_PAGE;

import SideNav from "./SideNav";
import SkeletonCard from "./SkeletonCard";
import MyUnpublishedApps from "./MyUnpublishedApps";
import NonProdPeopleApiWarning from "./NonProdPeopleApiWarning";
import DashboardsPanel from "./DashboardsPanel";
import { appCardDefinition } from "src/generic-components/AppCard";

import { fetchApps } from "../Ateam-BI/Templates/Utils/AppActivityUtils";
import { streamMyPublishedApps } from "../Ateam-BI/Templates/Utils/MyApplicationsUtils";
import {
  listFavorites,
  addFavorite,
  delFavorite,
  getFavorite,
} from "../utilities/Favorites";
import { listFeatured } from "src/utilities/Featured";
import { getUserRatings, submitRating, deleteRating } from "src/utilities/RatingAPIs";
import { getLocalCache, setLocalCache } from "src/utilities/CacheHelper";
import { MY_PUBLISHED_CACHE_KEY, cacheVariables } from "src/AppConstants/CacheConstants";

// Per-user cache key for the resolved "My Published" app_id list.
const publishedCacheKeyFor = (user) =>
  `${MY_PUBLISHED_CACHE_KEY}:${(user || "").toLowerCase()}`;

function MyPage() {
  const history = useHistory();
  const userId = useSelector((globalState) => globalState.auth.userId);

  const [activeTabId, setActiveTabId] = useState("my-applications");
  const [loader, setLoader] = useState(true);
  const [publishedLoader, setPublishedLoader] = useState(true);
  // True while published apps are still streaming in (after the first result
  // lands but before every supervisor-chain lookup has resolved).
  const [publishedStreaming, setPublishedStreaming] = useState(false);
  // Resolution progress for the published section: { done, total } owners.
  const [publishedProgress, setPublishedProgress] = useState({ done: 0, total: 0 });

  const [products, setProducts] = useState([]);
  const [publishedProducts, setPublishedProducts] = useState([]);
  const [favoritesPageIndex, setFavoritesPageIndex] = useState(1);
  const [publishedPageIndex, setPublishedPageIndex] = useState(1);

  const [favoritesMap, setFavoritesMap] = useState({});
  const [featuredMap, setFeaturedMap] = useState({});
  const [ratingsMap, setRatingsMap] = useState({});
  const [favDisabled, setFavDisabled] = useState(false);

  const getUrl = useCallback((item) => {
    if (item?.url) return item.url;
    return `/App/${item.app_id}`;
  }, []);

  // ---- Favorites -----------------------------------------------------------
  const loadFavorites = useCallback(() => {
    return new Promise((resolve) => {
      listFavorites(
        userId,
        (result) => {
          const items = result?.data?.listUserPreferences?.items || [];
          const map = {};
          items.forEach((item) => {
            map[item.preference_identifier] = true;
          });
          setFavoritesMap(map);
          setFavDisabled(false);
          resolve();
        },
        () => resolve()
      );
    });
  }, [userId]);

  const favorite = useCallback(
    async (appId) => {
      setFavDisabled(true);
      // Resolve current state from the source of truth to avoid stale toggles.
      getFavorite(
        appId,
        userId,
        async (result) => {
          const isFav =
            result?.data?.getUserPreferences?.preference_identifier === appId;
          if (isFav) {
            await delFavorite(
              appId,
              userId,
              () => {
                setFavoritesMap((prev) => {
                  const updated = { ...prev };
                  delete updated[appId];
                  return updated;
                });
              },
              () => {}
            );
          } else {
            await addFavorite(
              appId,
              userId,
              "default",
              () => {
                setFavoritesMap((prev) => ({ ...prev, [appId]: true }));
              },
              () => {}
            );
          }
          setFavDisabled(false);
        },
        () => setFavDisabled(false)
      );
    },
    [userId]
  );

  // ---- Featured ------------------------------------------------------------
  const loadFeatured = useCallback(() => {
    return new Promise((resolve) => {
      listFeatured(
        (result) => {
          const items = result?.data?.listUserPreferences?.items || [];
          const map = {};
          items.forEach((item) => {
            map[item.preference_identifier] = true;
          });
          setFeaturedMap(map);
          resolve();
        },
        () => resolve()
      );
    });
  }, []);

  // ---- Ratings -------------------------------------------------------------
  const handleRatingChange = useCallback(async (appId, rating) => {
    // Optimistic update — capture the previous value so a failed submit/delete
    // restores it (rather than clearing a rating the server still holds).
    let previousRating;
    setRatingsMap((prev) => {
      previousRating = prev[appId];
      return { ...prev, [appId]: rating };
    });

    const rollback = () =>
      setRatingsMap((prev) => {
        const copy = { ...prev };
        if (previousRating === undefined) {
          delete copy[appId];
        } else {
          copy[appId] = previousRating;
        }
        return copy;
      });

    if (rating === 0) {
      deleteRating(appId, "APP", () => {}, rollback);
    } else {
      submitRating(appId, "APP", rating, () => {}, rollback);
    }
  }, []);

  const loadRatings = useCallback(() => {
    if (!userId) return Promise.resolve();
    return new Promise((resolve) => {
      getUserRatings(
        "APP",
        (result) => {
          const ratings = result?.data?.getUserRatings || [];
          const map = {};
          ratings.forEach(({ entity_id, rating }) => {
            map[entity_id] = rating;
          });
          setRatingsMap(map);
          resolve();
        },
        () => resolve()
      );
    });
  }, [userId]);

  // ---- Apps + published computation ----------------------------------------
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!userId) return;
      setLoader(true);
      setPublishedLoader(true);

      // Favorites / featured / ratings can load in parallel with apps.
      loadFavorites();
      loadFeatured();
      loadRatings();

      let apps = [];
      try {
        apps = (await fetchApps()) || [];
      } catch (error) {
        console.error("MyPage: failed to fetch apps", error);
      }
      if (cancelled) return;
      setProducts(apps);
      setLoader(false);

      const orderByAppId = new Map(apps.map((app, index) => [app.app_id, index]));

      // Fast path: if we have a cached resolved app_id list for this user,
      // render the matching apps immediately — no per-owner PAPI lookups. The
      // app_id list is a small, stable result; we re-hydrate it against the
      // freshly fetched `apps` so card data is always current.
      const cacheConf = cacheVariables[MY_PUBLISHED_CACHE_KEY];
      if (cacheConf?.useCache) {
        const cachedIds = getLocalCache(publishedCacheKeyFor(userId), cacheConf.expiry);
        if (Array.isArray(cachedIds)) {
          const idSet = new Set(cachedIds);
          const cachedProducts = apps
            .filter((app) => idSet.has(app.app_id))
            .sort(
              (a, b) =>
                (orderByAppId.get(a.app_id) ?? 0) - (orderByAppId.get(b.app_id) ?? 0)
            );
          setPublishedProducts(cachedProducts);
          setPublishedLoader(false);
          setPublishedStreaming(false);
          return; // skip the live chain-lookup resolution
        }
      }

      // Stream "My Published Applications": append each qualifying app as its
      // supervisor-chain lookup resolves so cards appear progressively instead
      // of waiting for every lookup to finish. Results are kept in the original
      // fetched order via each app's index.
      setPublishedProducts([]);
      setPublishedStreaming(true);
      // Streaming has begun: drop the skeleton and let the ProgressBar drive
      // the loading affordance. Otherwise, if the user has apps but none
      // qualify, `onMatch` never fires and the skeleton would stay up forever
      // (hiding the ProgressBar, which only renders in the non-skeleton branch).
      setPublishedLoader(false);
      setPublishedProgress({ done: 0, total: 0 });
      const resolvedIds = [];
      try {
        const chainCache = {};
        await streamMyPublishedApps(
          apps,
          userId,
          (app) => {
            if (cancelled) return;
            resolvedIds.push(app.app_id);
            setPublishedProducts((prev) => {
              const next = [...prev, app];
              next.sort(
                (a, b) =>
                  (orderByAppId.get(a.app_id) ?? 0) - (orderByAppId.get(b.app_id) ?? 0)
              );
              return next;
            });
          },
          chainCache,
          (done, total) => {
            if (!cancelled) setPublishedProgress({ done, total });
          }
        );
        // Cache the resolved app_id list for fast repeat visits — but only if
        // this run finished uncancelled AND actually had apps to resolve. A
        // cancelled mid-stream run has a partial `resolvedIds`; and a transient
        // `fetchApps()` failure leaves `apps = []`, which would resolve to an
        // empty `resolvedIds` and poison the per-user cache (serving an empty
        // "My Published" list on the next visit even after apps recover).
        if (!cancelled && cacheConf?.useCache && apps.length > 0) {
          setLocalCache(publishedCacheKeyFor(userId), resolvedIds);
        }
      } catch (error) {
        console.error("MyPage: failed to compute published apps", error);
      } finally {
        // Always clear the skeleton and streaming indicator when this run ends,
        // even if it was cancelled — otherwise a cancelled run (e.g. effect
        // re-run / StrictMode double-invoke) could leave the spinner stuck.
        // A newer run re-sets `publishedStreaming` to true at its start, so an
        // extra clear here is harmless.
        setPublishedLoader(false);
        setPublishedStreaming(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userId, loadFavorites, loadFeatured, loadRatings]);

  const favoriteProducts = useMemo(
    () => products.filter((item) => favoritesMap[item.app_id]),
    [products, favoritesMap]
  );

  // Each section paginates independently: 2 rows per page, pager shown only
  // when there is more than one page (more than 2 rows of cards).
  const favoritesPagesCount = Math.max(
    1,
    Math.ceil(favoriteProducts.length / PAGE_SIZE)
  );
  const publishedPagesCount = Math.max(
    1,
    Math.ceil(publishedProducts.length / PAGE_SIZE)
  );

  // Keep each current page valid if its list shrinks (e.g. after data reloads).
  useEffect(() => {
    if (favoritesPageIndex > favoritesPagesCount) {
      setFavoritesPageIndex(favoritesPagesCount);
    }
  }, [favoritesPageIndex, favoritesPagesCount]);

  useEffect(() => {
    if (publishedPageIndex > publishedPagesCount) {
      setPublishedPageIndex(publishedPagesCount);
    }
  }, [publishedPageIndex, publishedPagesCount]);

  const favoritesPageItems = useMemo(() => {
    const start = (favoritesPageIndex - 1) * PAGE_SIZE;
    return favoriteProducts.slice(start, start + PAGE_SIZE);
  }, [favoriteProducts, favoritesPageIndex]);

  const publishedPageItems = useMemo(() => {
    const start = (publishedPageIndex - 1) * PAGE_SIZE;
    return publishedProducts.slice(start, start + PAGE_SIZE);
  }, [publishedProducts, publishedPageIndex]);

  const cardDefinition = useMemo(
    () =>
      appCardDefinition({
        favoritesMap,
        favDisabled,
        favorite,
        ratingsMap,
        handleRatingChange,
        getUrl,
        history,
        featuredMap,
      }),
    [favoritesMap, favDisabled, favorite, ratingsMap, handleRatingChange, getUrl, history, featuredMap]
  );

  const renderCards = (items, emptyTitle, emptyBody) => (
    <Cards
      className="apps-cards-container"
      cardDefinition={cardDefinition}
      cardsPerRow={[{ minWidth: 900, cards: 3 }]}
      items={items}
      loadingText="Loading resources"
      empty={
        <Box textAlign="center" color="inherit">
          <b>{emptyTitle}</b>
          <Box padding={{ bottom: "s" }} variant="p" color="inherit">
            {emptyBody}
          </Box>
        </Box>
      }
    />
  );

  const myApplicationsTab = (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            counter={!loader ? `(${favoriteProducts.length})` : undefined}
            description="Applications you've marked as favorites in the Marketplace"
          >
            My Favorites
          </Header>
        }
      >
        {loader ? (
          <Box padding={{ top: "l" }}>
            <SkeletonCard count={3} />
          </Box>
        ) : (
          <SpaceBetween size="m">
            {renderCards(favoritesPageItems, "No Favorites", "No favorites to display.")}
            {favoritesPagesCount > 1 && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Pagination
                  currentPageIndex={favoritesPageIndex}
                  pagesCount={favoritesPagesCount}
                  onChange={({ detail }) => setFavoritesPageIndex(detail.currentPageIndex)}
                  openEnd={false}
                />
              </div>
            )}
          </SpaceBetween>
        )}
      </Container>

      <NonProdPeopleApiWarning>
        People APIs data in lower environments is not up to date, so the
        ownership resolution behind <strong>My Published Applications</strong>{" "}
        (matching apps to you and your direct/indirect reports) may be
        inaccurate. This list may not reflect production data.
      </NonProdPeopleApiWarning>

      <Container
        header={
          <Header
            variant="h2"
            counter={!publishedLoader ? `(${publishedProducts.length})` : undefined}
            description="Applications owned by you or your direct and indirect reports"
          >
            My Published Applications
          </Header>
        }
      >
        {publishedLoader && !publishedStreaming ? (
          <Box padding={{ top: "l" }}>
            <SkeletonCard count={3} />
          </Box>
        ) : (
          <SpaceBetween size="m">
            {renderCards(
              publishedPageItems,
              "No Published Applications",
              "No applications owned by you or your reports to display."
            )}
            {publishedStreaming && (
              <Box padding={{ horizontal: "s" }}>
                <ProgressBar
                  value={
                    publishedProgress.total > 0
                      ? Math.round((publishedProgress.done / publishedProgress.total) * 100)
                      : 0
                  }
                  additionalInfo={
                    publishedProgress.total > 0
                      ? `${publishedProgress.done} of ${publishedProgress.total} owners checked`
                      : undefined
                  }
                  label="Loading more applications…"
                />
              </Box>
            )}
            {publishedPagesCount > 1 && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Pagination
                  currentPageIndex={publishedPageIndex}
                  pagesCount={publishedPagesCount}
                  onChange={({ detail }) => setPublishedPageIndex(detail.currentPageIndex)}
                  openEnd={false}
                />
              </div>
            )}
          </SpaceBetween>
        )}
      </Container>

      <MyUnpublishedApps />
    </SpaceBetween>
  );

  return (
    <AppLayout
      headerSelector="#h"
      navigation={<SideNav activeHref="/MyPage" />}
      toolsHide
      content={
        <Box padding={{ top: "s" }}>
          <Tabs
            tabs={[
              {
                label: "My Applications",
                id: "my-applications",
                content: myApplicationsTab,
              },
              {
                label: "Dashboards",
                id: "dashboards",
                content: <DashboardsPanel scope="user" />,
              },
            ]}
            activeTabId={activeTabId}
            onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
          />
        </Box>
      }
    />
  );
}

export default MyPage;
